import {useEffect, useState, useMemo} from "react";
import _ from "lodash";
import {useAtomValue, useAtom} from "jotai";
import {useTranslation} from "react-i18next";
import {Box, Button, ListItemIcon, Menu, MenuItem, Stack, Typography} from "@mui/material";
import AddIcon from "@mui/icons-material/AddOutlined";
import ArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import UploadIcon from "@mui/icons-material/FileUploadOutlined";
import {MainViewLayout} from "../MainViewLayout";
import {InsightsOverviewCard} from "./InsightsOverviewCard";
import {FarmHeader} from "./FarmHeader";
import {activeFarmFieldsLoadable} from "../../store/activeFarmFieldsStore";
import {FieldsInsightsTable} from "./FieldsInsightsTable";
import {useLoadableValueOrDefault} from "../../hooks/jotaiHooks";
import {useFieldsInsightsItems, useMergedFieldsInsightsItems} from "../../hooks/fieldInsightsItemsHooks";
import {routerCompanyIdAtom} from "../../store/routerStore";
import {isMaderaCompany} from "../../utils/maderaUtils";
import {useAccessMarker} from "../../hooks/accessMarkerHooks";
import {useActiveCompanyFields} from "../../hooks/fieldsHooks";
import {FieldCreateDialog} from "../dialogs/FieldCreateDialog";
import {DataDownloadDialog} from "../dialogs/DataDownloadDialog";
import {DataUploadDialog} from "../dialogs/DataUploadDialog";
import {ALL_ACCESS_FUNCTIONS} from "../../utils/accessControlUtils";
import {useActiveFarm} from "../../hooks/farmsHooks";
import {DataIcon} from "../icons/DataIcon";
import {MaderaParcelUnitTable} from "../madera/ParcelUnitTable";
import {activeMergedFarmIds} from "../../store/activeMergedFarmIds";
import {activeMergedFarmItems} from "../../store/activeMergedFarmItems";
import {getAllFields} from "../../services/fieldService";
import {Field} from "../../types/domain/field";
import {useCompany} from "../../hooks/companiesHooks";
import {useActiveFarmOverviewData} from "../../hooks/farmsHooks";

export const FarmInsightsView: React.FC = () => {
    const {t} = useTranslation();
    const companyId = useAtomValue(routerCompanyIdAtom);
    const company = useCompany();
    const farm = useActiveFarm();
    const isMadera = company?.data && isMaderaCompany(company?.data);
    const fields = useLoadableValueOrDefault(activeFarmFieldsLoadable, []);
    const {items} = useFieldsInsightsItems();
    const access = useAccessMarker(ACCESS_FUNCTIONS);
    const {refresh} = useActiveCompanyFields();
    const [activeMergedFarmIdsAtom] = useAtom(activeMergedFarmIds);
    const [dataToolsAnchorEl, setDataToolsAnchorEl] = useState<null | HTMLElement>(null);
    const [fieldCreateDialogOpened, setFieldCreateDialogOpened] = useState<boolean>(false);
    const [dataDownloadDialogOpened, setDataDownloadDialogOpened] = useState<boolean>(false);
    const [dataUploadDialogOpened, setDataUploadDialogOpened] = useState<boolean>(false);
    const overviewData = useActiveFarmOverviewData();
    const [_activeMergedFarmItems, setActiveMergedFarmItems] = useAtom<Field[]>(activeMergedFarmItems);
    const [loadedMergedFarmIds, setloadedMergedFarmIds] = useState<string[]>([]);

    /* IMPLEMENTED FOR MERGED FARMS */
    const _fields = useMemo(() => {
        if (company?.data?.includeChildCompanies && _activeMergedFarmItems?.length) {
            return _activeMergedFarmItems;
        }
        return fields;
    }, [company?.data?.includeChildCompanies, _activeMergedFarmItems, fields]);

    const farmsMergedItems = useMergedFieldsInsightsItems(_fields);

    const _items = useMemo(() => {
        if (company?.data?.includeChildCompanies) {
            return farmsMergedItems;
        }
        return items;
    }, [company?.data?.includeChildCompanies, farmsMergedItems, items]);

    useEffect(() => {
        if (
            company?.data?.id &&
            activeMergedFarmIdsAtom?.length &&
            !_.isEqual(loadedMergedFarmIds, activeMergedFarmIdsAtom)
        ) {
            setloadedMergedFarmIds(activeMergedFarmIdsAtom);
            void Promise.all(
                activeMergedFarmIdsAtom.map((farmId) => getAllFields({companyId: company?.data?.id, farmId})),
            ).then((results) => {
                setActiveMergedFarmItems(results.flat());
            });
        }
    }, [activeMergedFarmIdsAtom, company, companyId, setActiveMergedFarmItems, loadedMergedFarmIds]);
    /* END */

    const onFieldCreateDialogClose = (isSuccessful: boolean) => {
        setFieldCreateDialogOpened(false);
        if (isSuccessful) {
            refresh();
        }
    };

    const onDataDownloadDialogClose = () => {
        setDataDownloadDialogOpened(false);
        setDataToolsAnchorEl(null);
    };

    const onDataUploadDialogClose = () => {
        setDataUploadDialogOpened(false);
        setDataToolsAnchorEl(null);
    };

    const hasOverviewData = useMemo(() => {
        return !!(overviewData.isSuccess && overviewData.data.crops.length);
    }, [overviewData]);

    return (
        <MainViewLayout header={<FarmHeader />}>
            <Stack direction="column" gap="1.5rem">
                {isMadera && (
                    <Stack direction="column">
                        <MaderaParcelUnitTable />
                    </Stack>
                )}
                <Stack direction="column">
                    <Stack direction="row" height="4rem" alignItems="center">
                        <Typography fontSize={20} fontWeight={500} textTransform="uppercase">
                            {t("insights.farm_insights_view.overview")}
                        </Typography>
                    </Stack>
                    <Box padding="1.5rem" bgcolor="white">
                        {hasOverviewData ? (
                            <InsightsOverviewCard
                                overviewData={overviewData.isSuccess ? overviewData.data : undefined}
                                isLoading={overviewData.isLoading}
                            />
                        ) : (
                            <Typography>{t("insights.company_insights_view.no_fields_or_active_fields")}</Typography>
                        )}
                    </Box>
                </Stack>
                <Stack direction="column">
                    <Stack direction="row" height="4rem" alignItems="center" justifyContent="space-between">
                        <Typography fontSize={20} fontWeight={500} textTransform="uppercase">
                            {t("insights.farm_insights_view.fields")}
                        </Typography>
                        <Stack direction="row" gap="1rem">
                            {access.canCreateNewField && (
                                <Stack>
                                    <Button
                                        startIcon={<AddIcon />}
                                        size="large"
                                        onClick={() => setFieldCreateDialogOpened(true)}
                                    >
                                        {t("companies.farms_view.new_field")}
                                    </Button>
                                </Stack>
                            )}
                            <Stack>
                                <Button
                                    startIcon={
                                        <DataIcon
                                            sx={{
                                                stroke: "#303A8C",
                                                width: "1.125rem",
                                                height: "1.125rem",
                                            }}
                                        />
                                    }
                                    size="large"
                                    onClick={(e) => setDataToolsAnchorEl(e.currentTarget)}
                                    endIcon={<ArrowDownIcon />}
                                >
                                    {t("data_tools.name")}
                                </Button>
                                <Menu
                                    id="data-tools-menu"
                                    anchorEl={dataToolsAnchorEl}
                                    open={!!dataToolsAnchorEl}
                                    onClose={() => setDataToolsAnchorEl(null)}
                                >
                                    <MenuItem onClick={() => setDataDownloadDialogOpened(true)}>
                                        <ListItemIcon>
                                            <DownloadIcon />
                                        </ListItemIcon>
                                        {t("data_tools.download")}
                                    </MenuItem>
                                    {access.canUploadCompanyData && (
                                        <MenuItem onClick={() => setDataUploadDialogOpened(true)}>
                                            <ListItemIcon>
                                                <UploadIcon />
                                            </ListItemIcon>
                                            {t("data_tools.add")}
                                        </MenuItem>
                                    )}
                                </Menu>
                            </Stack>
                        </Stack>
                    </Stack>
                    <FieldsInsightsTable
                        insightsItems={_items}
                        companyId={companyId}
                        onRefresh={refresh}
                        canEditItem={access.canEditCompanyFields}
                    />
                </Stack>
            </Stack>

            {fieldCreateDialogOpened && companyId && (
                <FieldCreateDialog
                    companyId={companyId}
                    open={fieldCreateDialogOpened}
                    mode="new"
                    onClose={onFieldCreateDialogClose}
                />
            )}

            {dataDownloadDialogOpened && companyId && (
                <DataDownloadDialog
                    open={dataDownloadDialogOpened}
                    mode="farm"
                    onClose={onDataDownloadDialogClose}
                    companyId={companyId}
                    farmIds={company?.data?.includeChildCompanies ? loadedMergedFarmIds : farm?.id ? [farm?.id] : []}
                />
            )}

            {dataUploadDialogOpened && companyId && (
                <DataUploadDialog open={dataUploadDialogOpened} onClose={onDataUploadDialogClose} />
            )}
        </MainViewLayout>
    );
};

const ACCESS_FUNCTIONS = _.pick(ALL_ACCESS_FUNCTIONS, [
    "canEditCompanyFields",
    "canCreateNewField",
    "canUploadCompanyData",
]);
